import axios from 'axios';
import store from '@/store';
import { router } from '@/main';

const baseURL = `${process.env.VUE_APP_API_URL}`;

const getAuthHeader = (token) => {
    const devMode = store.state.user.devMode;
    return devMode ? { 'X-API-KEY': `${token}` } : { Authorization: `Bearer ${token}` };
};

const apiRequest = async (method, url, data = null, params = {}) => {
    if (store.getters['user/isInvalidLoggedInAccount']) {
        console.error('Account ID is not set');
        store.dispatch('user/logout');
        if (router.currentRoute.value.path !== '/') {
            router.push({ name: 'Login', query: { logout: 'true' } });
        }
        return;
    }
    try {
        return store.dispatch('user/apiCall', async (token) => {
            const config = {
                method,
                url: `${baseURL}/accounts/${store.state.user.user.account_id}/pipeline${url}`,
                headers: getAuthHeader(token),
                params: { ...params },
                data,
            };
            return await axios(config);
        });
    } catch (error) {
        throw error;
    }
};

const pipelineApiRepository = {
    /**
     * Get the full pipeline board (stages + first page of leads per stage).
     * @param {number} [size=20] - Page size per stage
     * @returns {Promise<Object>} Board: { source, enabled, stagesEditable, playbookSkillId, stages }
     */
    getBoard: async (size = 20) => {
        const response = await apiRequest('GET', '/board', null, { size });
        return response.data;
    },

    /**
     * Activate the native pipeline. Returns the same shape as getBoard.
     * @returns {Promise<Object>} Board
     */
    activate: async () => {
        const response = await apiRequest('POST', '/activate');
        return response.data;
    },

    /**
     * Get a page of leads for a stage.
     * @param {string} stageKey - Stage id (native) or stage name (Zoho)
     * @param {number} [page=0]
     * @param {number} [size=20]
     * @returns {Promise<Object>} { stage, leads, hasMore, total }
     */
    getStageLeads: async (stageKey, page = 0, size = 20) => {
        const response = await apiRequest('GET', `/stages/${encodeURIComponent(stageKey)}/leads`, null, {
            page,
            size,
        });
        return response.data;
    },

    /**
     * Search leads by name, phone or email across the whole pipeline, not only the loaded cards.
     * @param {string} query - At least 2 characters
     * @param {number} [size=50]
     * @returns {Promise<Object>} { leads, hasMore } — each lead carries the stage of its column
     */
    searchLeads: async (query, size = 50) => {
        const response = await apiRequest('GET', '/leads/search', null, { q: query, size });
        return response.data;
    },

    /**
     * Get a lead with its qualification fields.
     * @param {string} leadId
     * @returns {Promise<Object>} { card, fields }
     */
    getLead: async (leadId) => {
        const response = await apiRequest('GET', `/leads/${leadId}`);
        return response.data;
    },

    /**
     * Re-sync a lead with the external platform by hand (Live4Life today). The backend re-reads the
     * record there, brings their stage back and writes ours ignoring the sync fingerprint.
     *
     * Always resolves with 200: the outcome travels in `status`, because "they rejected it" and
     * "they are down" are answers to show, not request errors.
     *
     * @param {string} leadId
     * @returns {Promise<Object>} { status, ok, platform, externalId, detail, lead }
     */
    syncLead: async (leadId) => {
        const response = await apiRequest('POST', `/leads/${leadId}/sync`);
        return response.data;
    },

    /**
     * Move a lead to another stage (fires the lead playbook server-side).
     * @param {string} leadId
     * @param {string} toStage - Target stage NAME
     * @returns {Promise<Object>} The updated LeadCard
     */
    moveLead: async (leadId, toStage) => {
        const response = await apiRequest('POST', `/leads/${leadId}/move`, { toStage });
        return response.data;
    },

    /**
     * Delete a lead from the board (native mode only). The backend archives it, so the history is
     * kept and calling it twice is harmless.
     * @param {string} leadId
     * @returns {Promise<void>}
     */
    deleteLead: async (leadId) => {
        await apiRequest('DELETE', `/leads/${leadId}`);
    },

    /**
     * Create a stage (native mode only).
     * @param {string} name
     * @param {string} [color]
     * @returns {Promise<Object>} { id, name, order, color }
     */
    createStage: async (name, color) => {
        const body = color ? { name, color } : { name };
        const response = await apiRequest('POST', '/stages', body);
        return response.data;
    },

    /**
     * Update a stage name and/or color (native mode only).
     * @param {string} stageId
     * @param {{name?: string, color?: string}} changes
     * @returns {Promise<Object>} The updated stage
     */
    updateStage: async (stageId, changes) => {
        const response = await apiRequest('PATCH', `/stages/${stageId}`, changes);
        return response.data;
    },

    /**
     * Set the colour of a stage. The only stage edit allowed when the pipeline lives in an external
     * CRM: names, order and the stages themselves are the CRM's, the colour is Lixsa's.
     *
     * @param {string} stageKey - Stage id in native mode, stage name with an external CRM
     * @param {string|null} color - Hex colour, or null to clear it
     * @returns {Promise<Object>} The stored colours, keyed by stage
     */
    setStageColor: async (stageKey, color) => {
        const response = await apiRequest('PUT', `/stages/${encodeURIComponent(stageKey)}/color`, { color });
        return response.data;
    },

    /**
     * Reorder the stages (native mode only).
     * @param {string[]} stageIds - Stage ids in the new order
     * @returns {Promise<Object[]>} The reordered stages
     */
    reorderStages: async (stageIds) => {
        const response = await apiRequest('PUT', '/stages/order', { stageIds });
        return response.data;
    },

    /**
     * Delete a stage (native mode only). If it has leads, migrateTo is required.
     * @param {string} stageId
     * @param {string} [migrateTo] - Stage id that receives the leads
     * @returns {Promise<void>}
     */
    deleteStage: async (stageId, migrateTo) => {
        await apiRequest('DELETE', `/stages/${stageId}`, null, migrateTo ? { migrateTo } : {});
    },

    /**
     * Approved WhatsApp templates available for the lead playbook.
     * @returns {Promise<Object[]>} [{ uuid, name, language, status, body, variables }]
     */
    getTemplates: async () => {
        const response = await apiRequest('GET', '/templates');
        return response.data;
    },
};

export default pipelineApiRepository;
